import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel, { type FilterGroup } from "@/components/feature/FilterPanel";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import { movies } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";
import { setPageMeta } from "@/utils/seo";
import {
  defaultFilters,
  filterGroups,
  filterMovies,
  type FilterState,
} from "@/utils/filterMovies";

const PAGE_SIZE = 8;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();
  const [input, setInput] = useState(keyword);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const { isFavorite, toggle } = useFavorites();

  useEffect(() => {
    setInput(keyword);
    setPage(1);
  }, [keyword]);

  useEffect(() => {
    setPageMeta(
      `${keyword ? `搜索：${keyword}` : "搜索"}－日韩中文字幕在线观看`,
      `搜索${keyword ? `“${keyword}”` : ""}相关的日韩影视资源，支持中文字幕在线观看，可按地区、年份、评分筛选。`,
    );
  }, [keyword]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 220);
    return () => window.clearTimeout(timer);
  }, [keyword, filters, typeFilter, page]);

  const base = useMemo(() => {
    const kw = keyword.toLowerCase();
    if (!kw) return movies;
    return movies.filter(
      (movie) =>
        movie.title.toLowerCase().includes(kw) ||
        movie.originalTitle.toLowerCase().includes(kw) ||
        movie.director.toLowerCase().includes(kw) ||
        movie.cast.some((name) => name.toLowerCase().includes(kw)) ||
        movie.genres.some((genre) => genre.toLowerCase().includes(kw)) ||
        String(movie.year).includes(kw),
    );
  }, [keyword]);

  const result = useMemo(() => {
    const typed =
      typeFilter === "all"
        ? base
        : base.filter((movie) => movie.type === typeFilter);
    return filterMovies(typed, filters);
  }, [base, filters, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const pageItems = result.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const groups: FilterGroup[] = [
    {
      key: "type",
      label: "类型",
      options: [
        { label: "全部", value: "all" },
        { label: "电视剧", value: "电视剧" },
        { label: "电影", value: "电影" },
      ],
    },
    ...filterGroups,
  ];

  const handleChange = (key: string, value: string) => {
    if (key === "type") {
      setTypeFilter(value);
      setPage(1);
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setTypeFilter("all");
    setPage(1);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = input.trim();
    if (value) setSearchParams({ q: value });
    else setSearchParams({});
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-7xl px-4 pt-6 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

          <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h1 className="text-xl font-medium text-foreground-950 md:text-2xl">
              {keyword ? (
                <>
                  搜索：<span className="text-primary-700">{keyword}</span>
                </>
              ) : (
                "全站影视搜索"
              )}
            </h1>
            <form
              onSubmit={handleSubmit}
              className="mt-5 flex flex-col gap-3 sm:flex-row"
            >
              <div className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2.5 focus-within:border-primary-400">
                <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="输入片名、主演或题材，例如“鬼怪”“孔刘”“悬疑”"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                />
              </div>
              <button
                type="submit"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
            <p className="mt-4 text-sm text-foreground-600">
              共找到{" "}
              <span className="font-medium text-accent-600">{result.length}</span>{" "}
              部相关影视
              {keyword ? `（关键词：${keyword}）` : "（当前展示全部影视）"}
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6">
          <div className="flex flex-col gap-8 lg:flex-row">
            <aside className="w-full shrink-0 lg:w-64">
              <FilterPanel
                groups={groups}
                value={{ ...filters, type: typeFilter }}
                onChange={handleChange}
                onReset={handleReset}
              />
            </aside>

            <div className="min-w-0 flex-1">
              {loading ? (
                <div className="grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: PAGE_SIZE }).map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="aspect-[2/3] w-full rounded-lg bg-background-200" />
                      <div className="mt-3 h-4 w-3/4 rounded bg-background-200" />
                      <div className="mt-2 h-3 w-1/2 rounded bg-background-200" />
                    </div>
                  ))}
                </div>
              ) : (
                <MovieGrid
                  movies={pageItems}
                  emptyText={
                    keyword
                      ? `没有找到与“${keyword}”相关的影视内容`
                      : "暂无相关影视资源"
                  }
                  onReset={handleReset}
                  isFavorite={isFavorite}
                  onToggleFavorite={toggle}
                />
              )}
              {!loading && (
                <Pagination page={page} totalPages={totalPages} onChange={setPage} />
              )}
            </div>
          </div>

          {!loading && result.length === 0 && (
            <div className="mt-6 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 hover:bg-background-100"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-home-line text-base" />
                </span>
                返回首页看看热门推荐
              </Link>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}